import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { LoginService, resendVerificationOtp } from '../../Services/APIs/authentication';
import { setClinicAccessToken, setClinicDetails } from '../../Utils/clinicAuth';
import useResolveDestination from './useResolveDestination';
import AuthLayout from './AuthLayout';
import FlowErrorCard from './FlowErrorCard';

const errorClasses = "text-xs text-cv2-destructive mt-1";

const Login = () => {
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { resolveDestination, statusError, clearStatusError, isResolving } = useResolveDestination();

    const initialValues = {
        email: '',
        password: '',
    };

    const validationSchema = Yup.object().shape({
        email: Yup.string().email('Invalid email').required('Email is required'),
        password: Yup.string().required('Password is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        setSubmitSuccess('');
        setUnverifiedEmail('');
        try {
            const response = await LoginService({
                authenticate_email: values.email,
                password: values.password,
            });
            setSubmitSuccess(response.data?.message || 'Logged in successfully.');
            setClinicAccessToken(response.data?.data?.accessToken);
            setClinicDetails(response.data?.data?.clinic);
            await resolveDestination();
        } catch (error) {
            const message = error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.';
            setSubmitError(message);
            // two different 403s: only the unverified-email one carries the address back.
            // the other ("Password expired") needs password reset, not a verification code.
            const email = error?.response?.data?.data?.authenticate_email || error?.data?.data?.authenticate_email;
            if (error?.response?.status === 403 && email) {
                setUnverifiedEmail(email);
            }
        }
        setSubmitting(false);
    };

    const handleVerifyEmailClick = async () => {
        try {
            await resendVerificationOtp({ authenticate_email: unverifiedEmail });
        } catch (resendError) {
            console.error('Error resending verification OTP:', resendError);
        }
        navigate('/otp-verification', { state: { email: unverifiedEmail } });
    };

    if (statusError) {
        return (
            <FlowErrorCard
                variant="signin"
                message={statusError}
                onRetry={resolveDestination}
                isRetrying={isResolving}
                onLogout={() => {
                    // back to a clean form: submitSuccess still holds the now-void "Logged in
                    // successfully" from the attempt whose status check failed
                    clearStatusError();
                    setSubmitSuccess('');
                }}
            />
        );
    }

    return (
        <AuthLayout variant="signin">

                <div className="flex-1 flex flex-col justify-center">
                    <div className="mb-12">
                        <p className="cv2-auth-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-3.5">
                            Doctor Portal
                        </p>
                        <h1 className="font-aeonik font-normal text-[38px] leading-[44px] tracking-[-0.9px] text-cv2-foreground normal-case">
                            Dashboard<br />Login
                        </h1>
                    </div>

                    {submitSuccess && (
                        <div className="text-sm text-green-500 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2 mb-4">
                            {submitSuccess}
                        </div>
                    )}
                    {submitError && (
                        <div className="cv2-auth-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {submitError}
                            {unverifiedEmail && (
                                <button
                                    type="button"
                                    onClick={handleVerifyEmailClick}
                                    className="block mt-1 underline"
                                >
                                    Verify email
                                </button>
                            )}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="flex flex-col">
                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="email" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Email Address
                                    </label>
                                    <Field
                                        type="email"
                                        name="email"
                                        placeholder="you@practice.com"
                                        className="cv2-auth-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                    />
                                    <ErrorMessage name="email" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="password" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Password
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <Field
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            placeholder="••••••••"
                                            className="cv2-auth-input flex-1 bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className="cv2-auth-toggle shrink-0 text-cv2-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <ErrorMessage name="password" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t" />

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="mt-7 w-full h-12 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                >
                                    Sign In
                                </button>

                                <Link
                                    to="/forgot-password"
                                    className="cv2-auth-link mt-4 text-center font-spaceMono text-[10px] tracking-[0.8px] uppercase"
                                >
                                    Forgot Password?
                                </Link>
                            </Form>
                        )}
                    </Formik>
                </div>

                <div className="mt-10 flex flex-col gap-3.5">
                    <div className="cv2-auth-divider border-t" />
                    <p className="cv2-auth-join-copy font-aeonik text-[13px] text-center normal-case">
                        Don't have an account?
                    </p>
                    <Link
                        to="/signup"
                        className="cv2-auth-join-button w-full h-12 border rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] uppercase hover:border-claraview-secondary/40 hover:text-claraview-secondary transition-all flex items-center justify-center"
                    >
                        Join Claraview
                    </Link>
                </div>
        </AuthLayout>
    );
};

export default Login;
